import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAuthors, Author } from '@/services/api';

export const useDebouncedAuthors = (delay: number = 300) => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const searchTermRef = useRef<string>('');
  const isAuthorSelectedRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchTimeRef = useRef<number>(0);

  // Add a unique identifier to track hook instances
  const hookId = useRef(Math.random().toString(36).substr(2, 9));


  const debouncedSearch = useCallback((term: string) => {
    searchTermRef.current = term;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Check if enough time has passed since last search
    const now = Date.now();
    const timeSinceLastSearch = now - lastSearchTimeRef.current;

    if (timeSinceLastSearch < delay) {

      // Set new timeout

      const timeoutId = setTimeout(() => {

        performSearch(searchTermRef.current);
      }, delay - timeSinceLastSearch);

      timeoutRef.current = timeoutId;

    } else {

      performSearch(term);
    }
  }, [delay]);

  const performSearch = useCallback(async (term: string) => {


    if (term.length < 2) {

      setAuthors([]);
      setShowDropdown(false);
      return;
    }

    // Check if the current term matches the selected author
    if (isAuthorSelectedRef.current) {

      isAuthorSelectedRef.current = false;
    }


    setLoading(true);
    setShowDropdown(true);
    lastSearchTimeRef.current = Date.now();

    try {
      const data = await fetchAuthors(term);


      setAuthors(data || []);
    } catch (error) {
      console.error(`❌ [${hookId.current}] useDebouncedAuthors: API error:`, error);
      setAuthors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAuthorSelected = useCallback(() => {

    isAuthorSelectedRef.current = true;
  }, []);

  const clearAuthors = useCallback(() => {

    setAuthors([]);
    setShowDropdown(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {

    setAuthors([]);
    setShowDropdown(false);
    isAuthorSelectedRef.current = false;
    searchTermRef.current = '';
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);


  return {
    authors,
    loading,
    showDropdown,
    debouncedSearch,
    markAuthorSelected,
    clearAuthors,
    setShowDropdown,
    resetState
  };
};
